import React, { useState, useEffect, useCallback } from 'react';

const DEFAULT_MAPPINGS = {
  revenue: {
    RC_ROOM: 'Room Tariff Income',
    RC_LAUNDRY: 'Laundry Income',
    RC_ROOM_SERVICE: 'Room Service Income',
    RC_MINIBAR: 'Minibar Income',
    RC_KOT: 'F&B Income',
    RC_BAR: 'Bar Income',
    RC_LATE_CO: 'Late Checkout Income',
    RC_ALLOWANCE: 'Discount / Allowance',
    RC_MISC: 'Miscellaneous Income'
  },
  payment: {
    Cash: 'Cash',
    Card: 'Bank Account - Card',
    UPI: 'UPI Collections',
    Cheque: 'Cheque in Hand',
    'Mobile Wallet': 'Mobile Wallet'
  },
  tax: {
    CGST: 'CGST Output',
    SGST: 'SGST Output',
    IGST: 'IGST Output'
  },
  debtor: 'Sundry Debtors - Guests',
  advance: 'Advance from Guests',
  kot_sales: 'F&B Sales - KOT',
  bar_sales: 'Bar Sales'
};

const REVENUE_LABELS = {
  RC_ROOM: 'Room Tariff',
  RC_LAUNDRY: 'Laundry',
  RC_ROOM_SERVICE: 'Room Service',
  RC_MINIBAR: 'Minibar',
  RC_KOT: 'F&B / KOT',
  RC_BAR: 'Bar',
  RC_LATE_CO: 'Late Checkout',
  RC_ALLOWANCE: 'Allowance / Discount',
  RC_MISC: 'Miscellaneous'
};

const PAYMENT_LABELS = {
  Cash: 'Cash',
  Card: 'Card / POS',
  UPI: 'UPI',
  Cheque: 'Cheque',
  'Mobile Wallet': 'Mobile Wallet'
};

const TAX_LABELS = {
  CGST: 'CGST',
  SGST: 'SGST',
  IGST: 'IGST'
};

export default function AccountingExportSettings({ token, selectedProperty }) {
  const [companyName, setCompanyName] = useState('');
  const [mappings, setMappings] = useState(DEFAULT_MAPPINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  useEffect(() => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    fetch('/api/v1/accounting-export/config', { headers: headers() })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        setCompanyName(data.tally_company_name || '');
        const m = data.mappings || {};
        setMappings({
          revenue: { ...DEFAULT_MAPPINGS.revenue, ...(m.revenue || {}) },
          payment: { ...DEFAULT_MAPPINGS.payment, ...(m.payment || {}) },
          tax: { ...DEFAULT_MAPPINGS.tax, ...(m.tax || {}) },
          debtor: m.debtor || DEFAULT_MAPPINGS.debtor,
          advance: m.advance || DEFAULT_MAPPINGS.advance,
          kot_sales: m.kot_sales || DEFAULT_MAPPINGS.kot_sales,
          bar_sales: m.bar_sales || DEFAULT_MAPPINGS.bar_sales
        });
      })
      .catch(() => setError('Failed to load config'))
      .finally(() => setLoading(false));
  }, [selectedProperty, headers]);

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    setError('');
    try {
      const resp = await fetch('/api/v1/accounting-export/config', {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ tally_company_name: companyName, mappings })
      });
      if (!resp.ok) {
        const e = await resp.json();
        throw new Error(e.error || 'Save failed');
      }
      setMsg('Settings saved successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateRevenue = (key, val) => setMappings(prev => ({ ...prev, revenue: { ...prev.revenue, [key]: val } }));
  const updatePayment = (key, val) => setMappings(prev => ({ ...prev, payment: { ...prev.payment, [key]: val } }));
  const updateTax = (key, val) => setMappings(prev => ({ ...prev, tax: { ...prev.tax, [key]: val } }));

  const sectionStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 };
  const labelStyle = { fontWeight: 600, fontSize: 14, marginBottom: 8, color: 'var(--theme-text)' };
  const inputStyle = { width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 };
  const rowStyle = { display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0', borderBottom: '1px solid #f3f4f6' };
  const cellLeft = { width: 180, fontSize: 13, color: '#6b7280', flexShrink: 0 };
  const cellRight = { flex: 1 };

  if (!selectedProperty?.id) return <div style={{ padding: 24 }}>Select a property first.</div>;
  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;

  return (
    <div style={{ padding: 24, maxWidth: 700 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: '#111827' }}>Export Settings</h2>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280' }}>
        Map your PMS categories to Tally ledger names. These names must match your Tally company ledgers for successful import.
      </p>

      {msg && <div style={{ padding: 8, background: '#d1fae5', color: '#065f46', borderRadius: 4, marginBottom: 12, fontSize: 13 }}>{msg}</div>}
      {error && <div style={{ padding: 8, background: '#fee2e2', color: '#991b1b', borderRadius: 4, marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {/* Company Name */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Tally Company Name</div>
        <input style={inputStyle} value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Enter your Tally company name" />
      </div>

      {/* Revenue Mapping */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Revenue Ledger Mapping</div>
        {Object.entries(REVENUE_LABELS).map(([key, label]) => (
          <div key={key} style={rowStyle}>
            <div style={cellLeft}>{label}</div>
            <div style={cellRight}>
              <input style={inputStyle} value={mappings.revenue[key] || ''} onChange={e => updateRevenue(key, e.target.value)} placeholder={DEFAULT_MAPPINGS.revenue[key]} />
            </div>
          </div>
        ))}
      </div>

      {/* Payment Mapping */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Payment Ledger Mapping</div>
        {Object.entries(PAYMENT_LABELS).map(([key, label]) => (
          <div key={key} style={rowStyle}>
            <div style={cellLeft}>{label}</div>
            <div style={cellRight}>
              <input style={inputStyle} value={mappings.payment[key] || ''} onChange={e => updatePayment(key, e.target.value)} placeholder={DEFAULT_MAPPINGS.payment[key]} />
            </div>
          </div>
        ))}
      </div>

      {/* Tax Mapping */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Tax Ledger Mapping</div>
        {Object.entries(TAX_LABELS).map(([key, label]) => (
          <div key={key} style={rowStyle}>
            <div style={cellLeft}>{label}</div>
            <div style={cellRight}>
              <input style={inputStyle} value={mappings.tax[key] || ''} onChange={e => updateTax(key, e.target.value)} placeholder={DEFAULT_MAPPINGS.tax[key]} />
            </div>
          </div>
        ))}
      </div>

      {/* Other Accounts */}
      <div style={sectionStyle}>
        <div style={labelStyle}>Other Accounts</div>
        <div style={rowStyle}>
          <div style={cellLeft}>Guest Debtor Account</div>
          <div style={cellRight}>
            <input style={inputStyle} value={mappings.debtor || ''} onChange={e => setMappings(prev => ({ ...prev, debtor: e.target.value }))} placeholder={DEFAULT_MAPPINGS.debtor} />
          </div>
        </div>
        <div style={rowStyle}>
          <div style={cellLeft}>Advance from Guests</div>
          <div style={cellRight}>
            <input style={inputStyle} value={mappings.advance || ''} onChange={e => setMappings(prev => ({ ...prev, advance: e.target.value }))} placeholder={DEFAULT_MAPPINGS.advance} />
          </div>
        </div>
        <div style={rowStyle}>
          <div style={cellLeft}>KOT Sales Account</div>
          <div style={cellRight}>
            <input style={inputStyle} value={mappings.kot_sales || ''} onChange={e => setMappings(prev => ({ ...prev, kot_sales: e.target.value }))} placeholder={DEFAULT_MAPPINGS.kot_sales} />
          </div>
        </div>
        <div style={rowStyle}>
          <div style={cellLeft}>Bar Sales Account</div>
          <div style={cellRight}>
            <input style={inputStyle} value={mappings.bar_sales || ''} onChange={e => setMappings(prev => ({ ...prev, bar_sales: e.target.value }))} placeholder={DEFAULT_MAPPINGS.bar_sales} />
          </div>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} style={{
        padding: '10px 28px', background: saving ? '#9ca3af' : '#2563eb', color: '#fff',
        border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer'
      }}>
        {saving ? 'Saving...' : 'Save Settings'}
      </button>
    </div>
  );
}
